import { useCallback, useRef, useState } from "react";
import {
  AnalyzeSectionSimilarity200,
  AnalyzeSectionSimilarity200OneOf,
  AnalyzeSectionSimilarity200OneOfNine,
  AnalyzeSectionSimilarity200OneOfThree,
  AnalyzeSectionSimilarityBody,
} from "~/api/model";
import { getStoredAccessToken } from "~/helpers";

interface AnalysisProgress {
  current: number;
  total: number;
  message: string;
}

interface UseAnalyzeSectionSimilarityOptions {
  organizationId: string;
  projectId: string;
  onComplete?: (
    groups: AnalyzeSectionSimilarity200OneOfThree["groups"]
  ) => void;
  onProgress?: (progress: AnalysisProgress) => void;
  onError?: (error: string) => void;
}

export const useAnalyzeSectionSimilarity = ({
  organizationId,
  projectId,
  onComplete,
  onProgress,
  onError,
}: UseAnalyzeSectionSimilarityOptions) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState<AnalysisProgress>({
    current: 0,
    total: 0,
    message: "",
  });
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const analyze = useCallback(
    async (sectionIds: string[]) => {
      if (sectionIds.length === 0) return;

      // Cancel any ongoing analysis
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      setIsAnalyzing(true);
      setProgress({
        current: 0,
        total: sectionIds.length,
        message: "Starting AI analysis...",
      });
      setError(null);

      const requestBody: AnalyzeSectionSimilarityBody = {
        sectionIds,
      };

      const apiUrl = `${import.meta.env.VITE_BASE_API_URL}/organizations/${organizationId}/projects/${projectId}/sections/analyze-similarity`;
      const token = getStoredAccessToken();

      try {
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error("No response body");
        }

        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const dataStr = line.slice(6);
              try {
                const data: AnalyzeSectionSimilarity200 = JSON.parse(dataStr);

                if ("type" in data && data.type === "progress") {
                  const progressData = data as AnalyzeSectionSimilarity200OneOf;
                  const newProgress = {
                    current: progressData.current,
                    total: progressData.total,
                    message:
                      progressData.message ||
                      `Processing ${progressData.current}/${progressData.total}...`,
                  };
                  setProgress(newProgress);
                  onProgress?.(newProgress);
                } else if ("type" in data && data.type === "complete") {
                  const completeData =
                    data as AnalyzeSectionSimilarity200OneOfThree;
                  setIsAnalyzing(false);
                  onComplete?.(completeData.groups);
                  break;
                } else if ("type" in data && data.type === "error") {
                  const errorData =
                    data as AnalyzeSectionSimilarity200OneOfNine;
                  const errorMessage = errorData.message || "Analysis failed";
                  setError(errorMessage);
                  setIsAnalyzing(false);
                  onError?.(errorMessage);
                  break;
                }
              } catch (parseError) {
                console.error("Error parsing SSE data:", parseError);
              }
            }
          }
        }
      } catch (err) {
        // Don't set error if this was an abort
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }

        console.error("Error during AI analysis:", err);
        const isNetworkError =
          err instanceof TypeError && err.message.includes("network");
        const errorMessage = isNetworkError
          ? "Connection was interrupted. This may happen with large documents. Please try with fewer sections or contact support if the issue persists."
          : "Connection error. Please try again.";

        setError(errorMessage);
        setIsAnalyzing(false);
        onError?.(errorMessage);
      }
    },
    [organizationId, projectId, onComplete, onProgress, onError]
  );

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsAnalyzing(false);
    }
  }, []);

  return {
    analyze,
    cancel,
    isAnalyzing,
    progress,
    error,
  };
};
